import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { User } from '@/services/user-search.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { RoleType } from '../constants/role.constants';

export interface UsersPagination {
  items: User[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface CreateUserDto {
  firstName: string;
  lastName: string;
  email: string;
  active?: boolean;
}

export interface UpdateUserDto {
  firstName?: string;
  lastName?: string;
  active?: boolean;
  roles?: RoleType[];
}

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private oidcSecurityService = inject(OidcSecurityService);
  private http = inject(HttpClient);
  private apiUrl = `${window.location.origin}/api/users`;

  getUsers(page: number = 1, limit: number = 50, search?: string): Observable<UsersPagination> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) {
      params = params.set('name', search);
    }

    return this.oidcSecurityService.getIdToken().pipe(
      switchMap((idToken) =>
        this.http.get<UsersPagination>(this.apiUrl, {
          params,
          headers: { Authorization: `Bearer ${idToken}` },
        })
      )
    );
  }

  getUserById(id: string): Observable<User> {
    return this.oidcSecurityService.getIdToken().pipe(
      switchMap((idToken) =>
        this.http.get<User>(`${this.apiUrl}/${id}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
      )
    );
  }

  createUser(userData: CreateUserDto): Observable<User> {

    return this.oidcSecurityService.getIdToken().pipe(
      switchMap((idToken) =>
        this.http.post<User>(this.apiUrl, userData, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
      )
    );
  }

  updateUser(id: string, updates: UpdateUserDto): Observable<User> {
    return this.oidcSecurityService.getIdToken().pipe(
      switchMap((idToken) =>
        this.http.patch<User>(`${this.apiUrl}/${id}`, updates, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
      )
    );
  }

  deleteUser(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }
}
